import {
  captureBrowserEventContext,
  createBrowserOutbox,
  serializeBrowserEventContext,
  restoreBrowserEventContext,
  type BrowserOutbox,
  type BrowserEventContext,
  type CapturedEvent,
  type CapturedEventInput,
  type CaptureOptions,
  type SnowplowClient,
  type SnowplowIdentity,
} from '@nextmar/snowplow-sdk';
import logger from '@/utils/client-logger';
import { isInZeloolApp } from '@/utils/tools';
import { getEventAdapter } from '../adapters/eventAdapters';
import { createZeeloolSnowplowDataAdapters } from '../integrations/snowplow-sdk/adapters';
import { captureSensorsEventProperties } from '../providers/sensorProvider';
import { ProviderType, type Provider, type TrackingEvent } from '../types';
import { generateEventId } from '../utils/eventIdGenerator';
import { filterSensorsBusinessProperties } from '../utils/sensorsProperties';
import {
  createAnalyticsDeliveryQueue,
  type AnalyticsChannel,
  type AnalyticsDeliveryQueue,
  type ChannelResult,
} from './deliveryQueue';
import {
  getSensorsIdentityPhase,
  hasSensorsInitialized,
  isSensorsIdentityReady,
} from './identityGate';
import { loadAnalyticsScript } from './scriptLoader';

/** 初始化等待只用于诊断，不取消或替换仍pending的脚本Promise。 */
const INITIALIZATION_WAIT_MS = 15_000;
/** 确定失败后的重试间隔，成功前保持每次单飞。 */
const RETRY_DELAYS_MS = [1000, 2000, 5000, 10_000] as const;
/** 可选诊断每文档最多200条，仅透出白名单字段，不保存事件包。 */
const MAX_DIAGNOSTICS = 200;
/** 应用阶段诊断不代表collector接受或最终入库。 */
export interface BrowserAnalyticsDiagnostic {
  stage: string;
  result: string;
  reason?: string;
  sensorsEventId?: string | number;
  eventName: string;
  recordId?: string;
  documentId: string;
  runtime: 'browser';
  visibility: string;
  at: number;
  persisted?: boolean;
  channel?: AnalyticsChannel;
  state?: string;
  eid?: string;
  attemptId?: string;
  attemptNo?: number;
  bytes?: number;
  httpStatus?: number;
}
/** 新SDK的逐事件交付通知，结构兼容已安装旧版本的CaptureOptions。 */
interface DeliveryUpdate {
  stage: string;
  state?: string;
  reason?: string;
  persisted: boolean;
  eid?: string;
  sensorsEventId?: string | number;
  documentId?: string;
  visibility?: string;
  attemptId?: string;
  attemptNo?: number;
  bytes?: number;
  httpStatus?: number;
  at?: number;
}
/** 区分明确拒绝与允许本页内存接纳，不能仅看put的持久化boolean。 */
interface OutboxRegistration {
  accepted: boolean;
  persisted: boolean;
  reason?: string;
}
/** runtime前门控与持久接纳通知由SDK提供，旧onHandoff只诊断。 */
type ReliableCaptureOptions = CaptureOptions & {
  onBeforeHandoff?: () => boolean;
  onDeliveryUpdate?: (update: DeliveryUpdate) => void;
};
/** 配置由原initializeReporting effect提供，不在render猜测远程开关。 */
export interface BrowserAnalyticsConfiguration {
  client: SnowplowClient;
  sensors: Provider & { sensorsLogin?: (uuid: string, snowplowUserId?: string) => boolean };
  snowplow: Provider;
  onDiagnostic?: (diagnostic: BrowserAnalyticsDiagnostic) => void | Promise<void>;
}
/** 原生事件与业务事件共用一个容量/TTL边界。 */
export interface BrowserAnalyticsCaptureOptions {
  /** 离页时在捕获调用栈内进入SDK；不保证SDK未就绪或网络失败时送达。 */
  delivery?: 'immediate';
  sensorsEvent?: TrackingEvent;
  channels?: readonly AnalyticsChannel[];
  context?: BrowserEventContext;
  /** 整页离开时在当前调用栈开始交付；普通事件仍按队列预算发送。 */
  immediate?: boolean;
}
/** 可跨文档恢复的数据，不保存回调、Provider 或 SDK 句柄。 */
interface StoredAnalyticsRecord {
  input: CapturedEventInput;
  context: string;
  capturedAt: number;
  options: CaptureOptions;
  channels: AnalyticsChannel[];
  sensorsEvent?: TrackingEvent;
  expectedSensorsIdentity: string | null;
  identityBound?: boolean;
  /** runtime可能已经调用；该入口记录不得跨文档再次发送Snowplow。 */
  snowplowHandoff?: 'unknown';
}
/** 配置前保存输入及SDK私有context，配置后立即转换成捕获句柄。 */
interface CapturedRecord {
  id: string;
  stored: StoredAnalyticsRecord;
  restored: boolean;
  input: CapturedEventInput;
  options: CaptureOptions;
  context?: BrowserEventContext;
  handle?: CapturedEvent;
  rejected?: boolean;
  /** 一旦持久化过，更新unknown失败就必须阻止runtime调用。 */
  persisted?: boolean;
}
/** 每个文档的编排状态；SSR不会创建此对象。 */
interface BrowserAnalyticsState {
  queue: AnalyticsDeliveryQueue;
  outbox: BrowserOutbox<StoredAnalyticsRecord>;
  records: Map<string, CapturedRecord>;
  config?: BrowserAnalyticsConfiguration;
  timer?: ReturnType<typeof setTimeout>;
  /** 文档级online监听仅配置一次，dispose对称释放。 */
  onOnline?: () => void;
  /** 同一文档只装一组队列生命周期监听，恢复后清除离页标记。 */
  onPageHide?: () => void;
  onBeforeUnload?: () => void;
  unloadResetTimer?: ReturnType<typeof setTimeout>;
  onPageShow?: () => void;
  onVisibilityChange?: () => void;
  leaving: false | 'beforeunload' | 'pagehide';
  documentId: string;
  diagnosticCount: number;
  waitTimers: Set<ReturnType<typeof setTimeout>>;
  attempts: Partial<Record<AnalyticsChannel, Promise<void>>>;
  failures: Record<AnalyticsChannel, number>;
  nextAt: Record<AnalyticsChannel, number>;
  ready: Record<AnalyticsChannel, boolean>;
  /** 配置时允许一次必要bootstrap；后续失败仅随有效渠道需求恢复。 */
  bootstrap: Record<AnalyticsChannel, boolean>;
  bound: boolean;
  initialIdentity?: SnowplowIdentity;
  sequence: number;
  disposed: boolean;
}
/** 按窗口隔离单实例，不在服务端跨请求缓存事件。 */
const browserStates = new WeakMap<Window, BrowserAnalyticsState>();
/** 仅本站Reporting入口登记的捕获起点；不接受外部时间覆盖TTL。 */
const reportingCaptureTimes = new WeakMap<BrowserEventContext, number>();

/** 必须先判断浏览器，再调用读取location的App判定。 */
export function isBrowserAnalytics(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined' && !isInZeloolApp();
}

/** 深复制业务输入和adapted payload；失败不保留可变引用。 */
export function cloneAnalyticsValue<T>(value: T): T {
  return structuredClone(value);
}

/** 日志只包含稳定类别与计数，禁止身份和payload。 */
function diagnose(
  code: string,
  metadata: Readonly<Record<string, number | string | boolean>> = {},
): void {
  logger.warn(`[browserAnalytics] ${code}`, metadata);
}

/** observer失败、超量或业务敏感属性都不进入正常上报链路。 */
function diagnoseRecord(
  state: BrowserAnalyticsState,
  input: CapturedEventInput,
  stage: string,
  result: string,
  details: Partial<
    Pick<
      BrowserAnalyticsDiagnostic,
      | 'recordId'
      | 'reason'
      | 'persisted'
      | 'channel'
      | 'state'
      | 'eid'
      | 'sensorsEventId'
      | 'documentId'
      | 'visibility'
      | 'attemptId'
      | 'attemptNo'
      | 'bytes'
      | 'httpStatus'
      | 'at'
    >
  > = {},
): void {
  const observer = state.config?.onDiagnostic;
  if (!observer || state.diagnosticCount >= MAX_DIAGNOSTICS) return;
  state.diagnosticCount += 1;
  try {
    const sensorsEventId =
      details.sensorsEventId ??
      (input.kind === 'track' ? input.options?.sensorsEventId : undefined);
    const observed = observer(
      Object.freeze({
        stage,
        result,
        ...details,
        eventName: (input.kind === 'track' ? input.eventName : input.kind).slice(0, 128),
        sensorsEventId:
          typeof sensorsEventId === 'string' ? sensorsEventId.slice(0, 128) : sensorsEventId,
        documentId: details.documentId ?? state.documentId,
        runtime: 'browser',
        visibility: details.visibility ?? document.visibilityState,
        at: details.at ?? Date.now(),
      }),
    );
    // async观察器同样不允许产生未处理Promise拒绝。
    if (observed) void Promise.resolve(observed).catch(() => undefined);
  } catch {
    // 诊断观察器不产生新的业务事件，也不能阻断捕获或交付。
  }
}

/** 新SDK使用明确注册结果；旧SDK仅在确实拥有内存记录时兼容接纳。 */
function registerIngress(state: BrowserAnalyticsState, record: CapturedRecord): OutboxRegistration {
  const outbox = state.outbox as BrowserOutbox<StoredAnalyticsRecord> & {
    register?: (id: string, value: StoredAnalyticsRecord, expiresAt: number) => OutboxRegistration;
  };
  if (outbox.register) return outbox.register(record.id, record.stored, record.options.expiresAt!);
  const persisted = outbox.put(record.id, record.stored, record.options.expiresAt!);
  return { accepted: persisted || outbox.owns(record.id), persisted };
}

/** 获取当前文档状态；仅明确browser入口可以调用。 */
function getState(): BrowserAnalyticsState {
  let state = browserStates.get(window);
  if (!state) {
    state = {
      queue: createAnalyticsDeliveryQueue({ onDiagnostic: diagnose }),
      outbox: createBrowserOutbox<StoredAnalyticsRecord>('zeelool:ingress', (code, id?: string) =>
        diagnose(code, id ? { recordId: id } : {}),
      ),
      records: new Map(),
      waitTimers: new Set(),
      attempts: {},
      failures: { sensors: 0, snowplow: 0 },
      nextAt: { sensors: 0, snowplow: 0 },
      ready: { sensors: false, snowplow: false },
      bootstrap: { sensors: true, snowplow: true },
      bound: false,
      leaving: false,
      documentId: crypto.randomUUID(),
      diagnosticCount: 0,
      sequence: 0,
      disposed: false,
    };
    browserStates.set(window, state);
    const current = state;
    /** pagehide之后产生的普通事件仍需要在原调用栈交给SDK。 */
    current.onPageHide = () => {
      current.leaving = 'pagehide';
      current.queue.flushNow();
    };
    /** 可取消的beforeunload只影响本调用栈，真实pagehide仍保留到恢复。 */
    current.onBeforeUnload = () => {
      if (current.leaving !== 'pagehide') current.leaving = 'beforeunload';
      current.queue.flushNow();
      if (current.unloadResetTimer !== undefined) clearTimeout(current.unloadResetTimer);
      current.unloadResetTimer = setTimeout(() => {
        current.unloadResetTimer = undefined;
        if (current.leaving === 'beforeunload') current.leaving = false;
      }, 0);
    };
    current.onPageShow = () => {
      current.leaving = false;
    };
    current.onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') current.queue.flushNow();
    };
    window.addEventListener('pagehide', current.onPageHide);
    window.addEventListener('beforeunload', current.onBeforeUnload);
    window.addEventListener('pageshow', current.onPageShow);
    document.addEventListener('visibilitychange', current.onVisibilityChange);
    void current.outbox.recover((id, stored, expiresAt) => {
      if (
        current.disposed ||
        !stored ||
        !Array.isArray(stored.channels) ||
        stored.options?.expiresAt !== expiresAt ||
        !Number.isFinite(stored.capturedAt)
      )
        return false;
      // 未知runtime状态保留到原TTL，允许另一渠道独立恢复，禁止Snowplow盲目重放。
      if (stored.snowplowHandoff === 'unknown' && !stored.channels.includes('sensors')) return true;
      const context = restoreBrowserEventContext(stored.context);
      return context ? enqueueStoredRecord(current, id, stored, context, true) : false;
    });
  }
  return state;
}

/** 同步导入触发context；SignUp身份未建立时保留原context供队列重试。 */
function importRecord(state: BrowserAnalyticsState, record: CapturedRecord): void {
  if (
    !state.config ||
    record.handle ||
    record.rejected ||
    (record.restored && record.stored.snowplowHandoff === 'unknown')
  )
    return;
  try {
    const captureOptions: ReliableCaptureOptions = {
      ...record.options,
      onBeforeHandoff: () => {
        record.stored.snowplowHandoff = 'unknown';
        const registration = registerIngress(state, record);
        const { persisted } = registration;
        if (!registration.accepted || (!persisted && record.persisted)) {
          delete record.stored.snowplowHandoff;
          diagnoseRecord(state, record.input, 'runtime_gate', 'pending', {
            recordId: record.id,
            reason: registration.reason ?? 'ingress_update_failed',
            persisted: false,
          });
          return false;
        }
        record.persisted = persisted || record.persisted;
        diagnoseRecord(state, record.input, 'runtime_gate', 'unknown', {
          recordId: record.id,
          persisted,
        });
        return true;
      },
      onHandoff: () =>
        diagnoseRecord(state, record.input, 'runtime_handoff', 'handed-off', {
          recordId: record.id,
        }),
      onDeliveryUpdate: (update) => {
        // 只透传SDK标量白名单，保留wire/attempt现场关联和通知时刻。
        diagnoseRecord(state, record.input, update.stage, update.state ?? 'unknown', {
          recordId: record.id,
          reason: update.reason,
          persisted: update.persisted,
          state: update.state,
          eid: update.eid,
          sensorsEventId: update.sensorsEventId,
          documentId: update.documentId,
          visibility: update.visibility,
          attemptId: update.attemptId,
          attemptNo: update.attemptNo,
          bytes: update.bytes,
          httpStatus: update.httpStatus,
          at: update.at,
        });
        if (update.stage === 'wire_registered' && update.persisted)
          finishStoredChannel(state, record, 'snowplow');
      },
    };
    let result = state.config.client.captureEvent(record.input, captureOptions, record.context);
    if (
      result.captured === false &&
      result.reason === 'invalid_event' &&
      record.input.kind === 'signUp'
    ) {
      const { sensors, client } = state.config;
      if (!sensors.isInitialized()) return;
      const { uuid, customerId } = record.input.user;
      const user = createZeeloolSnowplowDataAdapters().getUser();
      // 延迟登录只允许当前认证账号，避免队列把登出或切换账号回滚。
      if (!user.isLoggedIn || user.uuid !== uuid || user.customerId !== customerId) {
        record.rejected = true;
        record.context = undefined;
        return;
      }
      try {
        if (!sensors.sensorsLogin?.(uuid, customerId)) return;
        // 缓存中的distinct_id不能证明本窗口SDK已完成登录。
        if (window.sensorsDataAnalytic201505?.store?.getDistinctId?.() !== uuid) return;
        if (createZeeloolSnowplowDataAdapters().getTrackerUserId?.() !== customerId) return;
      } catch {
        return;
      }
      result = client.captureEvent(
        record.input,
        { ...captureOptions, bindSignUpIdentity: true },
        record.context,
      );
    }
    if (result.captured) record.handle = result.event;
    else record.rejected = true;
    diagnoseRecord(state, record.input, 'sdk_capture', result.captured ? 'accepted' : 'rejected', {
      recordId: record.id,
      reason: result.captured === false ? result.reason : undefined,
    });
  } catch {
    record.rejected = true;
    diagnose('context_import_failed');
  }
  // 持续保留原始上下文，首次身份绑定后更新持久化快照。
}

/** 只在明确失败后设置退避；15秒pending不会清除共享Promise。 */
function startAttempt(
  state: BrowserAnalyticsState,
  channel: AnalyticsChannel,
  initialize: () => Promise<boolean>,
): void {
  if (state.attempts[channel] || state.nextAt[channel] > Date.now() || state.disposed) return;
  state.bootstrap[channel] = false;
  const waitTimer = setTimeout(() => {
    state.waitTimers.delete(waitTimer);
    if (!state.disposed)
      diagnose('initialization_pending', { channel, waitMs: INITIALIZATION_WAIT_MS });
  }, INITIALIZATION_WAIT_MS);
  state.waitTimers.add(waitTimer);
  state.attempts[channel] = Promise.resolve()
    .then(() => (state.disposed ? false : initialize()))
    .then(
      (ready) => {
        if (state.disposed) return;
        state.ready[channel] = ready;
        if (!ready)
          state.nextAt[channel] =
            Date.now() +
            RETRY_DELAYS_MS[Math.min(state.failures[channel]++, RETRY_DELAYS_MS.length - 1)];
      },
      () => {
        if (!state.disposed)
          state.nextAt[channel] =
            Date.now() +
            RETRY_DELAYS_MS[Math.min(state.failures[channel]++, RETRY_DELAYS_MS.length - 1)];
      },
    )
    .finally(() => {
      clearTimeout(waitTimer);
      state.waitTimers.delete(waitTimer);
      delete state.attempts[channel];
      if (!state.disposed) {
        state.queue.wake();
        pump(state);
      }
    });
}

/** 神策初始化→现有/缓存ID或SDK_fpid→Classic→Client；渠道交付各自独立。 */
function pump(state: BrowserAnalyticsState): void {
  if (state.disposed || !state.config) return;
  if (state.timer !== undefined) clearTimeout(state.timer);
  state.timer = undefined;
  const { sensors, snowplow, client } = state.config;
  const snowplowDemand = snowplow.isEnabled && state.queue.hasPending('snowplow');
  const sensorsDemand = sensors.isEnabled && (state.queue.hasPending('sensors') || snowplowDemand);
  if (sensors.isEnabled && !state.ready.sensors && (sensorsDemand || state.bootstrap.sensors))
    startAttempt(state, 'sensors', () => sensors.init());
  if (
    snowplow.isEnabled &&
    !state.ready.snowplow &&
    (snowplowDemand || state.bootstrap.snowplow) &&
    hasSensorsInitialized() &&
    isSensorsIdentityReady()
  ) {
    // 所有配置前记录已经同步导入；只允许首次匿名ID绑定，不补登录身份。
    if (!state.bound) {
      state.bound = client.bindInitialIdentity();
      if (state.bound) {
        state.initialIdentity = cloneAnalyticsValue(
          createZeeloolSnowplowDataAdapters().getIdentity?.() ?? {},
        );
        for (const record of Array.from(state.records.values())) {
          if (
            record.options.identityPhase !== 'initial' ||
            record.stored.identityBound ||
            !record.context
          )
            continue;
          const serialized = serializeBrowserEventContext(record.context, {
            ...createZeeloolSnowplowDataAdapters(),
            getIdentity: () => state.initialIdentity!,
          });
          if (serialized) record.stored.context = serialized;
          record.stored.identityBound = true;
          record.persisted =
            state.outbox.put(record.id, record.stored, record.options.expiresAt!) ||
            record.persisted;
        }
      }
    }
    if (state.bound)
      startAttempt(state, 'snowplow', async () => {
        await loadAnalyticsScript('classic');
        // 迟到脚本成功不能恢复已销毁文档的runtime。
        if (state.disposed) return false;
        return snowplow.init();
      });
  }
  if ((sensorsDemand && !state.ready.sensors) || (snowplowDemand && !state.ready.snowplow)) {
    state.timer = setTimeout(() => pump(state), 250);
  }
}

/** 首次配置同步导入所有旧context后才启动初始化；重复配置不改变首次合同。 */
export function configureBrowserAnalytics(config: BrowserAnalyticsConfiguration): void {
  if (!isBrowserAnalytics()) return;
  const state = getState();
  if (state.config) return;
  state.config = config;
  /** 网络恢复可提前重试有效需求；空队列不启动bootstrap或恢复timer。 */
  state.onOnline = () => {
    if (!state.queue.hasPending('sensors') && !state.queue.hasPending('snowplow')) return;
    state.nextAt = { sensors: 0, snowplow: 0 };
    state.queue.wake();
    pump(state);
  };
  window.addEventListener('online', state.onOnline);
  for (const record of Array.from(state.records.values())) importRecord(state, record);
  state.queue.wake();
  pump(state);
}

/** 每个渠道只在其接管边界删除副本；另一渠道可独立恢复。 */
function finishStoredChannel(
  state: BrowserAnalyticsState,
  record: CapturedRecord,
  channel: AnalyticsChannel,
): void {
  record.stored.channels = record.stored.channels.filter((value) => value !== channel);
  if (record.stored.channels.length)
    record.persisted =
      state.outbox.put(record.id, record.stored, record.options.expiresAt!) || record.persisted;
  else state.outbox.remove(record.id);
}

/** 原始捕获和跨文档恢复共用入队逻辑，恢复不重新读取页面或延长 TTL。 */
function enqueueStoredRecord(
  state: BrowserAnalyticsState,
  id: string,
  stored: StoredAnalyticsRecord,
  context: BrowserEventContext,
  restored: boolean,
): boolean {
  if (
    state.initialIdentity &&
    stored.options.identityPhase === 'initial' &&
    !stored.identityBound
  ) {
    const serialized = serializeBrowserEventContext(context, {
      ...createZeeloolSnowplowDataAdapters(),
      getIdentity: () => state.initialIdentity!,
    });
    const reboundContext = serialized ? restoreBrowserEventContext(serialized) : null;
    if (reboundContext) {
      stored.context = serialized!;
      stored.identityBound = true;
      // 恢复可能晚于首次绑定，SDK 必须收到补齐后的私有上下文。
      context = reboundContext;
    }
  }
  const record: CapturedRecord = {
    id,
    stored,
    restored,
    input: stored.input,
    options: stored.options,
    context,
    persisted: restored,
  };
  const registration = registerIngress(state, record);
  if (!registration.accepted) {
    diagnoseRecord(state, record.input, 'app_enqueue', 'rejected', {
      recordId: id,
      reason: registration.reason ?? 'ingress_rejected',
      persisted: false,
    });
    return false;
  }
  record.persisted = registration.persisted || record.persisted;
  const accepted = state.queue.enqueue({
    id,
    capturedAt: stored.capturedAt,
    channels: stored.channels.filter(
      (channel) => !(restored && stored.snowplowHandoff === 'unknown' && channel === 'snowplow'),
    ),
    async deliver(channel): Promise<ChannelResult> {
      const config = state.config;
      if (!config) return 'pending';
      const status = (
        state.outbox as BrowserOutbox<StoredAnalyticsRecord> & {
          status?: (id: string) => string;
        }
      ).status?.(id);
      // 读取失败没有证明失权；保留同一捕获句柄、快照与原TTL等待恢复。
      if (status === 'unreadable' || (!status && !state.outbox.owns(id))) {
        diagnoseRecord(state, record.input, 'ingress_read', 'pending', {
          recordId: id,
          channel,
          reason: 'outbox_unreadable',
        });
        return 'pending';
      }
      if (status && status !== 'durable' && status !== 'memory') return 'rejected';
      if (!config[channel].isEnabled) {
        finishStoredChannel(state, record, channel);
        return 'rejected';
      }
      if (!state.ready[channel]) {
        diagnoseRecord(state, record.input, 'channel_ready', 'pending', {
          recordId: id,
          channel,
          reason: 'not_ready',
        });
        return 'pending';
      }
      if (channel === 'snowplow') {
        importRecord(state, record);
        const result = record.handle
          ? await config.client.submitCapturedEvent(record.handle)
          : { status: record.rejected ? ('rejected' as const) : ('pending' as const) };
        // handed-off/uncertain只能消费本页交付资格，持久入口由wire通知或TTL处理。
        if (result.status === 'rejected' && !stored.snowplowHandoff)
          finishStoredChannel(state, record, channel);
        diagnoseRecord(state, record.input, 'sdk_submit', result.status, {
          recordId: id,
          channel,
          reason: 'reason' in result ? result.reason : undefined,
        });
        return result.status;
      }
      if (!stored.sensorsEvent || !config.sensors.deliverCaptured) return 'rejected';
      if (restored) {
        const identity = createZeeloolSnowplowDataAdapters().getIdentity();
        if (identity.distinctId !== (stored.expectedSensorsIdentity ?? identity.anonymousId)) {
          diagnose('restored_sensors_identity_changed');
          finishStoredChannel(state, record, channel);
          return 'rejected';
        }
      }
      const result = config.sensors.deliverCaptured(cloneAnalyticsValue(stored.sensorsEvent));
      if (result !== 'pending') finishStoredChannel(state, record, channel);
      return result;
    },
    onRemove(reason) {
      if (record.handle && reason !== 'completed')
        state.config?.client.cancelCapturedEvent(
          record.handle,
          reason === 'expired' ? 'expired' : 'canceled',
        );
      if (
        reason !== 'disposed' &&
        !(reason === 'completed' && record.stored.channels.includes('snowplow'))
      )
        state.outbox.remove(id);
      diagnoseRecord(state, record.input, 'app_queue', reason, { recordId: id });
      state.records.delete(id);
      if (state.records.size === 0) {
        state.bootstrap = { sensors: false, snowplow: false };
        for (const timer of Array.from(state.waitTimers)) clearTimeout(timer);
        state.waitTimers.clear();
      }
      pump(state);
    },
  });
  if (!accepted) {
    state.outbox.remove(id);
    diagnoseRecord(state, record.input, 'app_enqueue', 'rejected', {
      recordId: id,
      reason: 'queue_rejected',
    });
    return false;
  }
  state.records.set(id, record);
  diagnoseRecord(state, record.input, 'app_enqueue', 'accepted', { recordId: id });
  diagnoseRecord(state, record.input, 'ingress_put', record.persisted ? 'persisted' : 'volatile', {
    recordId: id,
    persisted: record.persisted === true,
  });
  if (state.config) importRecord(state, record);
  pump(state);
  return true;
}

/** 捕获时立刻冻结事件及身份阶段；返回true仅表示进入本站有界内存队列。 */
export function captureBrowserAnalytics(
  input: CapturedEventInput,
  options: BrowserAnalyticsCaptureOptions = {},
): boolean {
  if (!isBrowserAnalytics()) return false;
  try {
    const state = getState();
    const capturedAt = options.context
      ? (reportingCaptureTimes.get(options.context) ?? Date.now())
      : Date.now();
    // SignUp 必须经过既有 Sensors 登录和 Tracker uid 绑定，不能被 Cookie 兜底提前放行。
    const context =
      options.context ??
      captureBrowserEventContext(createZeeloolSnowplowDataAdapters(input.kind !== 'signUp'));
    // 保留原神策业务字段空值过滤；公共属性的undefined必须显式覆盖未来动态值。
    const sensorProperties = filterSensorsBusinessProperties(
      options.sensorsEvent?.properties ?? {},
    );
    const sensorsEvent = options.sensorsEvent
      ? cloneAnalyticsValue({
          ...options.sensorsEvent,
          properties: { ...captureSensorsEventProperties(), ...sensorProperties },
        })
      : undefined;
    const channels: readonly AnalyticsChannel[] =
      options.channels ?? (sensorsEvent ? ['sensors', 'snowplow'] : ['snowplow']);
    const id = crypto.randomUUID();
    const adapters = createZeeloolSnowplowDataAdapters();
    const expectedIdentity = adapters.getIdentity?.().distinctId ?? adapters.getUser?.().uuid;
    const stored: StoredAnalyticsRecord = {
      input: cloneAnalyticsValue(input),
      context: serializeBrowserEventContext(context) ?? '',
      capturedAt,
      options: { identityPhase: getSensorsIdentityPhase(), expiresAt: capturedAt + 1_800_000 },
      channels: [...channels],
      sensorsEvent,
      expectedSensorsIdentity: typeof expectedIdentity === 'string' ? expectedIdentity : null,
    };
    const accepted = enqueueStoredRecord(state, id, stored, context, false);
    const lifecycleDelivery = state.leaving || document.visibilityState === 'hidden';
    if (accepted && (options.delivery === 'immediate' || lifecycleDelivery))
      state.queue.deliverNow(id);
    if (accepted && (options.immediate || lifecycleDelivery)) state.queue.flushNow();
    return accepted;
  } catch {
    diagnose('capture_failed');
    return false;
  }
}

/** 触发时适配两渠道；不依赖Provider注册和初始化，保留同一个eventId。 */
export function captureReportingAnalytics(
  event: TrackingEvent,
  providers?: ProviderType[],
  adaptionData?: unknown,
  adapterType?: string,
): boolean {
  if (!isBrowserAnalytics()) return false;
  const targets = !providers?.length ? [ProviderType.SENSORS, ProviderType.SNOWPLOW] : providers;
  const sensorsTargeted = targets.includes(ProviderType.SENSORS);
  const snowplowTargeted = sensorsTargeted || targets.includes(ProviderType.SNOWPLOW);
  if (!sensorsTargeted && !snowplowTargeted) return false;
  try {
    const capturedAt = Date.now();
    const context = captureBrowserEventContext(createZeeloolSnowplowDataAdapters());
    reportingCaptureTimes.set(context, capturedAt);
    const eventId = event.eventId ?? generateEventId();
    const source = cloneAnalyticsValue({ ...event, eventId });
    const data = cloneAnalyticsValue(adaptionData);
    const adapter = adapterType ? getEventAdapter(adapterType) : undefined;
    if (adapterType && !adapter) return false;
    /** 每个adapter调用独立复制，第三方适配或神策改写不会污染另一渠道。 */
    const adapt = (type: ProviderType, targeted: boolean): TrackingEvent[] => {
      if (!targeted) return [];
      const value = adapter ? adapter(cloneAnalyticsValue(data), type, eventId) : source;
      return value
        ? cloneAnalyticsValue(
            (Array.isArray(value) ? value : [value]).map((adapted) => ({
              ...adapted,
              eventId: adapted.eventId ?? eventId,
            })),
          )
        : [];
    };
    const sensors = adapt(ProviderType.SENSORS, sensorsTargeted);
    const snowplow = adapt(ProviderType.SNOWPLOW, snowplowTargeted);
    let accepted = false;
    for (let index = 0; index < Math.max(sensors.length, snowplow.length); index++) {
      const sp = snowplow[index];
      const se = sensors[index];
      const channels: AnalyticsChannel[] = [
        ...(se ? ['sensors' as const] : []),
        ...(sp ? ['snowplow' as const] : []),
      ];
      const sourceEvent = sp ?? se;
      accepted =
        captureBrowserAnalytics(
          {
            kind: 'track',
            eventName: sourceEvent.eventName ?? '',
            properties: sourceEvent.properties ?? {},
            options: {
              sensorsEventId: sourceEvent.eventId ?? eventId,
              omitPixel: sourceEvent.options?.omitPixel,
            },
          },
          { sensorsEvent: se, channels, context },
        ) || accepted;
    }
    return accepted;
  } catch {
    diagnose('adaptation_failed');
    return false;
  }
}

/** 文档卸载/测试隔离使用；迟到初始化与交付不再恢复任何记录。 */
export function disposeBrowserAnalytics(): void {
  if (typeof window === 'undefined') return;
  const state = browserStates.get(window);
  if (!state) return;
  state.disposed = true;
  if (state.onOnline) window.removeEventListener('online', state.onOnline);
  if (state.onPageHide) {
    window.removeEventListener('pagehide', state.onPageHide);
  }
  if (state.onBeforeUnload) window.removeEventListener('beforeunload', state.onBeforeUnload);
  if (state.unloadResetTimer !== undefined) clearTimeout(state.unloadResetTimer);
  if (state.onPageShow) window.removeEventListener('pageshow', state.onPageShow);
  if (state.onVisibilityChange)
    document.removeEventListener('visibilitychange', state.onVisibilityChange);
  if (state.timer !== undefined) clearTimeout(state.timer);
  for (const timer of Array.from(state.waitTimers)) clearTimeout(timer);
  state.waitTimers.clear();
  state.queue.dispose();
  state.outbox.dispose();
  state.records.clear();
  browserStates.delete(window);
}
